import React, { useState, useEffect } from 'react';

import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import { t } from 'src/shared/literals';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import Payment from 'src/payment/models/Payment';

import createPayment from 'src/payment/use-cases/createPayment';
import getPaymentById from 'src/payment/use-cases/getPaymentById';
import updatePaymentById from 'src/payment/use-cases/updatePaymentById';
import deletePaymentById from 'src/payment/use-cases/deletePaymentById';

import getPaymentStatusList from 'src/payment-status/use-cases/getPaymentStatusList';
import getPaymentTypeList from 'src/payment-type/use-cases/getPaymentTypeList';

import getEmployeeList from 'src/employee/use-cases/getEmployeeList';
import getCustomerContactList from 'src/customer-contact/use-cases/getCustomerContactList';

import getCustomerInvoiceList from 'src/customer-invoice/use-cases/getCustomerInvoiceList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  cancelPaymentButton: {
    top: '-20px',
    marginRight: theme.spacing(1),
    color: 'red'
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const PaymentForm = ({ customerTermId, cancel, success }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const user = JSON.parse(localStorage.user);
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;

  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);

  const initFormFields = async (fields, resetFields) => {
    let customerContactList = [];
    let invoiceBillToCustomerContactId = null;

    if (fields && Object.keys(fields).length) {
      const invoiceNumberValue = fields.invoiceNumber;

      let customerInvoiceList;

      if (invoiceNumberValue && invoiceNumberValue > 1) {
        customerInvoiceList = await getCustomerInvoiceList({
          companyHeadquarterId,
          customerId: null,
          search: invoiceNumberValue,
          page: null,
          orderBy: ''
        });
      }

      let customerInvoice;
      if (customerInvoiceList && customerInvoiceList.count) {
        console.log(customerInvoiceList);
        customerInvoice = customerInvoiceList.rows[0];
        fields.amount = fields.id ? fields.amount : customerInvoice.amount;
        fields.moneyCollectionCustomerContactId =
          customerInvoice.moneyCollectionCustomerContactId;

        console.log(customerInvoice);
      }

      const headquarterCustomerId =
        fields?.headquarterCustomerId || customerInvoice.headquarterCustomerId;

      if (headquarterCustomerId) {
        customerContactList = await getCustomerContactList({
          headquarterCustomerId,
          page: null,
          limit: null,
          orderBy: ''
        });

        const billContact = customerContactList.rows.filter(
          (x) => x?.document === fields?.document || customerInvoice?.document
        );
        if (billContact.length) {
          invoiceBillToCustomerContactId = billContact[0].id;
        }
      }
    }

    const paymentTypeList = getPaymentTypeList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const paymentStatusList = getPaymentStatusList({
      page: null,
      limit: null,
      orderBy: ''
    });

    const employeeList = getEmployeeList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    Promise.all([paymentTypeList, paymentStatusList, employeeList]).then(
      (responseList) => {
        const instance = Payment({
          fields,
          user,
          paymentTypeList: responseList[0],
          paymentStatusList: responseList[1],
          employeeList: responseList[2].rows,
          customerContactList: customerContactList.count
            ? customerContactList.rows
            : [],
          invoiceBillToCustomerContactId
        });

        if (resetFields) {
          setFormFields([]);
        }

        setFormFields(instance);
      }
    );
  };

  const fetchForm = async () => {
    if (paymentId) {
      const record = await getPaymentById(paymentId);
      initFormFields(record);
    }
  };

  useEffect(() => {
    if (paymentId === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );

    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues, target) => {
    if (target.name === 'invoiceNumber') {
      let fields = {};
      formFieldsValues.forEach((x) => {
        const item = {
          [x.name]: x.value
        };
        fields = {
          ...fields,
          ...item
        };

        // Reset
        fields.amount = '0';
        fields.customerContactId = null;
      });

      initFormFields(fields);
    } else if (target.name === 'paymentTypeId') {
      let fields = {};
      formFieldsValues.forEach((x) => {
        const item = {
          [x.name]: x.value
        };
        fields = {
          ...fields,
          ...item
        };
      });

      initFormFields(fields, true);

      return;
    } else if (target.name === 'customerContactId') {
      let fields = {};
      formFieldsValues.forEach((x) => {
        const item = {
          [x.name]: x.value
        };
        fields = {
          ...fields,
          ...item
        };
      });

      initFormFields(fields, true);

      return;
    }

    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    validatedForm.fields.companyHeadquarterId = companyHeadquarterId;

    setIsRequesting(true);
    let result;
    if (paymentId === 'new') {
      result = await createPayment(validatedForm.fields);
    } else {
      result = await updatePaymentById(validatedForm.fields, paymentId);
    }

    setIsRequesting(false);

    if (typeof result.id === 'undefined') {
      if (t(result.error)) {
        alert(t(result.error));
      } else {
        alert('Se ha producido un error, vuelva a intentarlo');
      }
    } else {
      navigate('/app/payments', { replace: true });
    }
  };

  const cancelPayment = async () => {
    if (isRequesting) {
      return;
    }

    setIsRequesting(true);
    const result = await deletePaymentById(paymentId);
    setIsRequesting(false);

    console.log(result);
    if (result.error) {
      if (t(result.error)) {
        alert(t(result.error));
      } else {
        alert('Se ha producido un error, vuelva a intentarlo');
      }
    } else {
      alert('Pago anulado correctamente');
      navigate('/app/payments', { replace: true });
    }
  };

  return (
    <Page className={classes.root} title="Cobranzas">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="" onClick={cancel}>
              Pago
            </Link>
            {` > ${
              formFields.length && formFields[0].value.length
                ? formFields[0].value
                : ''
            }-${
              formFields.length && formFields[1].value.length
                ? formFields[1].value
                : ''
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Button className={classes.backButton} onClick={cancel}>
            {'Volver'}
          </Button>

          {paymentId && user && user.scopes.includes('delete:payments') && (
            <Button
              className={classes.cancelPaymentButton}
              onClick={cancelPayment}
            >
              {'Anular pago'}
            </Button>
          )}

          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default PaymentForm;
